import React, { ReactNode, useMemo, useState } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

import { PlaygroundOutputCodeBlock } from '@/Pages/Playground/components/PlaygroundOutputCodeBlock';
import { MAX_TEXT_LENGTH } from '@/components/Code/constants';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Text } from '@/design-system';
import { Button } from '@/design-system/components/Button/Button';
import { cn } from '@/utils/tailwind';

import { TextVirtualizer } from '../TextVirtualizer';
import { TextWithLinks } from '../TextWithLinks';

// Markdown component configurations using design system components
const getMarkdownComponents = (): Components => ({
  // Code blocks with syntax highlighting
  pre: (props) => {
    const codeElement = (props.children as any)?.props?.children;
    const className = (props.children as any)?.props?.className || '';
    const language = className.replace('language-', '') || 'text';
    const code =
      typeof codeElement === 'string' ? codeElement : String(codeElement || '');

    return (
      <div className="w-full">
        <PlaygroundOutputCodeBlock code={code} language={language} />
      </div>
    );
  },
  a: (props) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className="text-ls-blue underline hover:opacity-80"
    />
  ),
  h1: ({ children, ...rest }) => (
    <Text {...rest} variant="h1" color="primary">
      {children}
    </Text>
  ),
  h2: ({ children, ...rest }) => (
    <Text {...rest} variant="h2" color="primary">
      {children}
    </Text>
  ),
  h3: ({ children, ...rest }) => (
    <Text {...rest} variant="h3" color="primary">
      {children}
    </Text>
  ),
  h4: ({ children, ...rest }) => (
    <Text {...rest} variant="h4" color="primary">
      {children}
    </Text>
  ),
  h5: ({ children, ...rest }) => (
    <Text {...rest} variant="md" color="primary">
      {children}
    </Text>
  ),
  h6: ({ children, ...rest }) => (
    <Text {...rest} variant="sm" color="primary">
      {children}
    </Text>
  ),
  // Style blockquotes
  blockquote: (props) => (
    <blockquote
      {...props}
      className="bg-secondary/20 my-4 border-l-4 border-secondary py-2 pl-4 text-secondary"
    />
  ),
  // Style lists with standard behavior
  ul: (props) => <ul {...props} className="ml-6 list-disc space-y-1" />,
  ol: (props) => <ol {...props} className="ml-6 list-decimal space-y-1" />,
  // Unwrap a sole <p> inside list items to avoid marker/content splitting
  li: ({ children, ...rest }) => {
    const childrenArray = Array.isArray(children) ? children : [children];
    const only = childrenArray.length === 1 ? childrenArray[0] : null;
    if (only && (only as React.ReactElement)?.type === 'p') {
      const inner = (only as React.ReactElement)?.props?.children;
      // Render without the paragraph wrapper
      return <li {...rest}>{inner}</li>;
    }
    return (
      <li {...rest}>
        <div className="flex w-full flex-col items-start">{children}</div>
      </li>
    );
  },
  // Style tables
  table: (props) => (
    <table
      {...props}
      className="w-auto border-collapse border border-secondary text-sm"
    />
  ),
  th: (props) => (
    <th
      {...props}
      className="bg-secondary/20 border border-secondary px-1 py-0.5 text-left text-xs font-medium"
    />
  ),
  td: (props) => (
    <td {...props} className="border border-secondary px-1 py-0.5 text-xs" />
  ),
  // Inline code with design system styling
  code: (props) => (
    <code
      {...props}
      className="bg-secondary/40 rounded px-1 py-0.5 font-mono text-xs"
    >
      {props.children}
    </code>
  ),
  p: ({ children, ...rest }) => (
    <Text {...rest} variant="body" color="primary">
      {children}
    </Text>
  ),
});

// Keep plugin arrays stable across renders to avoid remounting ReactMarkdown
const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [];

export interface MarkdownRendererProps {
  content: string;
  containerHeight?: string;
  className?: string;
  toolCallsComponents?: ReactNode;
}

/**
 * Component for rendering string content with markdown support and smart fallback.
 *
 * Features:
 * - Full markdown rendering with design system components
 * - Automatic truncation for long content with "Show more/less" toggle
 * - Virtualization fallback using TextVirtualizer for very long content
 * - Consistent styling with the design system
 */
export function MarkdownRenderer({
  content,
  containerHeight,
  className,
  toolCallsComponents,
}: MarkdownRendererProps) {
  const [isTruncated, setIsTruncated] = useState(true);

  const componentsMemo = useMemo(() => getMarkdownComponents(), []);

  // Determine if content is long enough to need special handling
  const isTextLong = content.length > MAX_TEXT_LENGTH;

  // Apply truncation if needed
  const contentToRender =
    isTextLong && isTruncated
      ? `${content.slice(0, MAX_TEXT_LENGTH)}...`
      : content;

  // Helper to safely render markdown with ErrorBoundary fallback
  const renderMarkdown = (text: string) => (
    <ErrorBoundary fallback={() => <TextWithLinks text={text} />}>
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={componentsMemo}
      >
        {text}
      </ReactMarkdown>
    </ErrorBoundary>
  );

  // For large content with container height, use TextVirtualizer fallback
  if (isTextLong && containerHeight) {
    // Custom renderer for markdown chunks in virtualized list
    const renderMarkdownChunk = (chunk: string) => renderMarkdown(chunk);

    return (
      <div className={cn('text-sm', className)}>
        <TextVirtualizer
          text={contentToRender}
          containerHeight={`calc(${containerHeight} - 35px)`}
          renderChunk={renderMarkdownChunk}
        />
        {toolCallsComponents}
      </div>
    );
  }

  // For normal content, render markdown directly
  return (
    <div className={cn('text-sm', className)}>
      {renderMarkdown(contentToRender)}
      {isTextLong && (
        <Button
          variant="underlined"
          size="sm"
          color="secondary"
          className="mt-2"
          onClick={() => setIsTruncated(!isTruncated)}
        >
          {isTruncated ? 'Show more' : 'Show less'}
        </Button>
      )}
      {toolCallsComponents}
    </div>
  );
}
